import { UserRole } from 'app-types';
import { useGetDeskInfo, useGetSubscriptionInfo, useGetUser, useMyAccess } from 'data-layer';
import {
  ChatSidebarIcon,
  DeskSidebarIcon,
  HomeIcon,
  InvoiceChecking,
  MultipleSettings,
} from 'icons';
import { FormattedMessage } from 'localization';

import {
  EnumAdvancedToolsRoutes,
  EnumDeskRoutes,
  EnumGeneralSettingRoutes,
  EnumSettingRoutes,
  InvoiceCheckingTabRoutes,
} from '../../constants';
import { SettingsSidebarMenuItem } from './AdminSettingsSidebar';
import { useBrandingSection } from './sidebar-options/brandingSection';
import { chatSection } from './sidebar-options/chatSection';
import { adminRoles, deskSection } from './sidebar-options/deskSection';
import { fulfillmentSection } from './sidebar-options/fulfillmentSection';
import { generalSettingsSection } from './sidebar-options/generalSettingsSection';
import { notificationsSection } from './sidebar-options/notificationsSection';
import { returnsAndExchangesSection } from './sidebar-options/returnsAndExchangesSection';
import { shippingSection } from './sidebar-options/shippingSection';
import { threePLProviderSection, threePLSection } from './sidebar-options/threePLSection';
import { trackingSection } from './sidebar-options/trackingSection';

// Tag a section with its first-level settings-section key (see src/lib/roles.js).
const withKey = (
  section: SettingsSidebarMenuItem,
  sectionKey: string,
): SettingsSidebarMenuItem => ({ ...section, sectionKey });

// General Settings is one sidebar section but spans several access keys: User
// Management → `users`, Roles & Permissions → `rolesAndPermissions`, personal
// User Settings → always available (no key), everything else → `generalSettings`.
const tagGeneralItems = (items: SettingsSidebarMenuItem['data']): SettingsSidebarMenuItem['data'] =>
  items?.map((item) => ({
    ...item,
    sectionKey: item.value.includes(EnumGeneralSettingRoutes.userManagement)
      ? 'users'
      : item.value.includes(EnumGeneralSettingRoutes.rolesAndPermissions)
      ? 'rolesAndPermissions'
      : item.value.includes(EnumGeneralSettingRoutes.userSettings)
      ? undefined
      : 'generalSettings',
  }));

const useGetAdminMenuItems = () => {
  const { brandingSection } = useBrandingSection();
  const { data: userData, isLoading: isLoadingUser } = useGetUser();
  const { data: subscriptionInfo, isLoading: isLoadingSubscription } = useGetSubscriptionInfo();
  const { hasAccessToDesk } = useGetDeskInfo(subscriptionInfo?.subscription);
  const { canAccessModule, canAccessSection, loading: accessLoading } = useMyAccess();

  const menuItems: SettingsSidebarMenuItem[] = [];

  const isAdmin = adminRoles.some((role) => userData?.roles.includes(role));
  const isCustomerSupport = userData?.roles.includes(UserRole.customerSupport);
  // Desk Support Tasks is gated by a store feature flag (not roles): hide its
  // settings entry unless enabled.
  const enableDeskTasks = Boolean(userData?.storeSettings?.featureFlags?.enableDeskTasks);

  // Show a section to system admins (`isAdmin`) OR to any user whose effective
  // access includes the section. System non-admin roles (warehouse/CS) have no
  // settings sections, so this is purely additive: it lets CUSTOM roles see the
  // sections their access grants, without changing system-role behaviour.
  const canSee = (sectionKey: string) => isAdmin || canAccessSection(sectionKey);

  if (subscriptionInfo?.subscription.isActive) {
    if (canSee('fulfillmentSettings'))
      menuItems.push(withKey(fulfillmentSection, 'fulfillmentSettings'));
    if (canSee('shippingSettings')) menuItems.push(withKey(shippingSection, 'shippingSettings'));
    if (canSee('trackingSettings')) menuItems.push(withKey(trackingSection, 'trackingSettings'));
    if (canSee('notificationsSettings'))
      menuItems.push(withKey(notificationsSection, 'notificationsSettings'));
    if (canSee('returnsSettings'))
      menuItems.push(withKey(returnsAndExchangesSection, 'returnsSettings'));
    if (canSee('invoiceCheckingSettings'))
      menuItems.push({
        sectionKey: 'invoiceCheckingSettings',
        translationKey: 'dashboard.invoiceChecking',
        data: [
          {
            value: `/${InvoiceCheckingTabRoutes.invoice}/${InvoiceCheckingTabRoutes.checking}`,
            label: <FormattedMessage id="settings.invoice-settings" />,
          },
        ],
        hasDivider: true,
        oldApp: false,
        icon: <InvoiceChecking width={24} height={24} />,
      });
  }

  if (hasAccessToDesk && canAccessModule('desk')) {
    menuItems.push({
      // No section-level key: the "Outvio Desk" group shows for any user with
      // the `desk` module. Personal items (My Profile, Personal Folders — they
      // carry no `roles`) stay visible even without `deskSettings`; org-config
      // items are individually gated by the `deskSettings` settings section.
      icon: <DeskSidebarIcon width={24} height={24} />,
      data: [...deskSection]
        // Hide the Support Task Folders entry unless the feature flag is on.
        .filter(
          (item) => enableDeskTasks || !item.value.includes(EnumSettingRoutes.supportTaskFolders),
        )
        .filter(
          (item) =>
            !item.roles ||
            item.roles.some((role) => userData?.roles.includes(role)) ||
            // Custom roles carry no system staff role; let `deskSettings` access
            // surface the org-config items (the final access gate re-checks it).
            canAccessSection('deskSettings'),
        )
        .map((item) => (item.roles ? { ...item, sectionKey: 'deskSettings' } : item)),
      title: 'Outvio Desk',
    });

    if (canSee('chatSettings')) {
      menuItems.push({
        sectionKey: 'chatSettings',
        hasDivider: !!userData?.storeSettings.is3pl,
        icon: <ChatSidebarIcon width={24} height={24} />,
        data: chatSection,
        translationKey: 'general.outvio-chat',
      });
    }
  }

  if (subscriptionInfo?.subscription.isActive) {
    if (isAdmin || isCustomerSupport || canAccessSection('advancedToolsSettings')) {
      menuItems.push({
        sectionKey: 'advancedToolsSettings',
        translationKey: 'general.advanced-tools',
        icon: <MultipleSettings width={24} height={24} />,
        data: [
          {
            value: `/${EnumDeskRoutes.settings}/${EnumSettingRoutes.advancedTools}/${EnumAdvancedToolsRoutes.cashOnDelivery}`,
            label: <FormattedMessage id="general.cash-on-delivery" />,
          },
          {
            value: `/${EnumDeskRoutes.settings}/${EnumSettingRoutes.advancedTools}/${EnumAdvancedToolsRoutes.ordersExport}`,
            label: <FormattedMessage id="settings.adv-tools.orders-export.title" />,
          },
        ],
      });
    }

    if (canSee('threePlSettings')) {
      menuItems.push({
        sectionKey: 'threePlSettings',
        data: userData?.storeSettings.is3pl ? threePLProviderSection : threePLSection,
        title: '3PL',
        icon: <HomeIcon width={24} height={24} />,
        hasDivider: true,
      });
    }
  }

  if (subscriptionInfo?.subscription.isActive) {
    if (canSee('branding')) menuItems.push(withKey(brandingSection, 'branding'));
    // General Settings is always shown; its items are tagged per access key
    // (users / rolesAndPermissions / generalSettings; personal User Settings has
    // no key and is always visible) and narrowed by the final access gate below.
    menuItems.push({
      ...generalSettingsSection,
      data: tagGeneralItems(generalSettingsSection.data),
    });
  } else {
    // Inactive subscription: General Settings without user management / integrations.
    menuItems.push({
      ...generalSettingsSection,
      data: tagGeneralItems(
        generalSettingsSection.data?.filter(
          (item) =>
            !item.value.includes(EnumGeneralSettingRoutes.userManagement) &&
            !item.value.includes(EnumGeneralSettingRoutes.integrations),
        ),
      ),
    });
  }

  // Final access gate: hide sections/items the user's effective access doesn't
  // include. Skipped while access is still loading to avoid an empty flash;
  // `isLoading` covers that window. Section/item without a key is never gated.
  const gatedMenuItems = accessLoading
    ? menuItems
    : menuItems
        .map((section) => ({
          ...section,
          data: section.data?.filter(
            (item) => !item.sectionKey || canAccessSection(item.sectionKey),
          ),
        }))
        .filter(
          (section) =>
            (!section.sectionKey || canAccessSection(section.sectionKey)) &&
            (section.data === undefined || section.data.length > 0),
        );

  return {
    menuItems: gatedMenuItems,
    isLoading: isLoadingSubscription || isLoadingUser || accessLoading,
  };
};

export { useGetAdminMenuItems };
